import {
  createContext,
  useContext,
  useEffect,
  useState
} from "react"

import {
  getSession,
  onAuthStateChange,
  loadCurrentUser
} from "./authService"

const AuthContext = createContext()

export function AuthProvider({ children }) {

  // --------------------------------------------------
  // AUTHENTICATED USER
  //
  // The person actually signed into Supabase.
  // This NEVER changes just because an admin/founder
  // switches the active BluPrint profile.
  // --------------------------------------------------

  const [authenticatedUser, setAuthenticatedUser] = useState(null)

  // --------------------------------------------------
  // ACTIVE BLUPRINT USER
  //
  // The profile currently being viewed/used in BluPrint.
  //
  // Normal user:
  // authenticatedUser === currentUser
  //
  // Admin viewing another profile:
  // authenticatedUser !== currentUser
  // --------------------------------------------------

  const [currentUser, setCurrentUser] = useState(null)

  const [loading, setLoading] = useState(true)

  console.log("AUTHENTICATED USER:", authenticatedUser)
  console.log("ACTIVE BLUPRINT USER:", currentUser)

  // --------------------------------------------------
  // Update fields on ACTIVE BluPrint user
  // --------------------------------------------------

  const updateCurrentUser = (updates) => {

    setCurrentUser(current => {

      if (!current) {
        return current
      }

      return {
        ...current,
        ...updates
      }

    })

  }

  // --------------------------------------------------
  // Initialize authentication
  // --------------------------------------------------

  useEffect(() => {

    let mounted = true

    async function initialize() {

      try {

        const {
          data: { session }
        } = await getSession()

        console.log(
          "AUTH INITIAL SESSION:",
          session?.user?.id,
          session?.user?.email
        )

        if (!mounted) {
          return
        }

        if (session) {

          console.log(
            "AUTH INITIALIZING USER:",
            session.user.id,
            session.user.email
          )

          const profile =
            await loadCurrentUser(session)

          if (mounted) {

            // This is the actual signed-in person.
            setAuthenticatedUser(profile)

            // Initial load starts with the authenticated
            // profile. App-level restoreActiveProfile()
            // may immediately replace this with a valid
            // saved admin profile.
            setCurrentUser(profile)

          }

        } else {

          setAuthenticatedUser(null)
          setCurrentUser(null)

        }

      } catch (error) {

        console.error(
          "AUTH INITIALIZE FAILED",
          error
        )

        if (mounted) {

          setAuthenticatedUser(null)
          setCurrentUser(null)

        }

      } finally {

        if (mounted) {
          setLoading(false)
        }

      }

    }

    initialize()

    // ------------------------------------------------
    // Listen for Supabase authentication changes
    // ------------------------------------------------

    const {
      data: { subscription }
    } = onAuthStateChange(
      async (event, session) => {

        console.log(
          "AUTH STATE CHANGE:",
          event,
          session?.user?.id,
          session?.user?.email
        )

        // ------------------------------------------------
        // IMPORTANT:
        //
        // INITIAL_SESSION is NOT a new login.
        //
        // Supabase sends INITIAL_SESSION after our
        // getSession() initialization above.
        //
        // We MUST NOT reset currentUser here because
        // restoreActiveProfile() may already have restored
        // the active admin/founder profile.
        // ------------------------------------------------

        if (event === "INITIAL_SESSION") {

          console.log(
            "AUTH INITIAL_SESSION: preserving active BluPrint user"
          )

          return
        }

        try {

          // ------------------------------------------------
          // Actual sign-out
          // ------------------------------------------------

          if (!session) {

            if (mounted) {

              setAuthenticatedUser(null)
              setCurrentUser(null)

            }

            return
          }

          // ------------------------------------------------
          // Actual authentication change
          //
          // Examples:
          // SIGNED_IN
          // TOKEN_REFRESHED
          // USER_UPDATED
          // SIGNED_OUT is handled above
          //
          // A real login/logout boundary is allowed to
          // reset the active BluPrint profile.
          // ------------------------------------------------

          const profile =
            await loadCurrentUser(session)

          if (mounted) {

            setAuthenticatedUser(profile)

            // Only a REAL login establishes a new active
            // BluPrint profile.
            //
            // TOKEN_REFRESHED and USER_UPDATED must NOT
            // overwrite the active profile. An admin
            // may currently be viewing another profile.
            if (event === "SIGNED_IN") {
              setCurrentUser(profile)
            }

          }

        } catch (error) {

          console.error(
            "AUTH STATE CHANGE FAILED",
            error
          )

          if (mounted) {

            setAuthenticatedUser(null)
            setCurrentUser(null)

          }

        }

      }
    )

    return () => {

      mounted = false
      subscription.unsubscribe()

    }

  }, [])

  // --------------------------------------------------
  // Reload the AUTHENTICATED user's profile
  //
  // IMPORTANT:
  // This does NOT overwrite an active admin/founder
  // profile.
  // --------------------------------------------------

  async function refreshAuthenticatedUser() {

    try {

      const {
        data: { session }
      } = await getSession()

      if (!session) {

        setAuthenticatedUser(null)
        setCurrentUser(null)

        return null
      }

      const profile =
        await loadCurrentUser(session)

      setAuthenticatedUser(profile)

      // Only replace currentUser when the active profile
      // is the authenticated profile.
      //
      // If an admin/founder is currently viewing another
      // BluPrint profile, leave that active profile alone.
      setCurrentUser(current => {

        if (!current) {
          return profile
        }

        if (current.id === profile.id) {
          return profile
        }

        return current

      })

      return profile

    } catch (error) {

      console.error(
        "REFRESH AUTHENTICATED USER FAILED",
        error
      )

      return null

    }

  }

  // --------------------------------------------------
  // Backwards-compatible name
  // --------------------------------------------------

  async function refreshCurrentUser() {
    return refreshAuthenticatedUser()
  }

  return (

    <AuthContext.Provider
      value={{
        // Actual signed-in person
        authenticatedUser,

        // Active BluPrint profile
        currentUser,

        loading,

        setCurrentUser,
        updateCurrentUser,

        refreshCurrentUser,
        refreshAuthenticatedUser
      }}
    >

      {children}

    </AuthContext.Provider>

  )

}

export function useAuth() {
  return useContext(AuthContext)
}